import { IconButton, InputAdornment, SelectChangeEvent, TextField } from "@mui/material";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import errorIcon from "../../assets/icons/error-icon.svg";
import instantmixIcon from "../../assets/icons/instantmix-icon.svg";
import CloseIconRed from "../../assets/icons/close-icon-red.svg";
import searchIcon from "../../assets/icons/search-icon.svg";
import MultiSelectDropdown from "../../shared/components/multi-select-dropdown/MultiSelectDropdown";

interface SegmentsFiltersProps {
  inputNameValue: string;
  handleStatusChange: (e: SelectChangeEvent<string[]>) => void;
  handleCreatedByChange: (e: SelectChangeEvent<string[]>) => void;
  handleLastEditedChange: (e: SelectChangeEvent<string[]>) => void;
  handleBrandChange: (e: SelectChangeEvent<string[]>) => void;
  status: string[];
  createdBy: string[];
  lastEditedBy: string[];
  brand: string[];
  statusOptions: string[];
  createdByOptions: string[];
  lastEditedByOptions: string[];
  brandOptions: string[];

  clearFilters: () => void;
}

const SegmentsFilters = ({
  handleStatusChange,
  handleCreatedByChange,
  handleLastEditedChange,
  handleBrandChange,
  status,
  createdBy,
  lastEditedBy,
  brand,
  statusOptions,
  createdByOptions,
  lastEditedByOptions,
  brandOptions,
  clearFilters,
}: SegmentsFiltersProps) => {
  const methods = useFormContext();
  const { control } = methods;

  const [areFiltersVisible, setAreFiltersVisible] = useState(false);

  const toggleFiltersVisibility = () => {
    setAreFiltersVisible((prev) => !prev);
  };

  return (
    <div className="segments__container__filters-wrap">
      <div className="segments__container__filters-wrap__left">
        <div className="segments__container__filters-wrap__input">
          <Controller
            control={control}
            name="search"
            rules={{
              required: false,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <label ref={field.ref} className="filter-label" htmlFor="search-segment">
                  <TextField
                    {...field}
                    id="search-segment"
                    className="form-control-container__text-field"
                    margin="none"
                    fullWidth
                    variant="outlined"
                    placeholder="Search segment"
                    slotProps={{
                      input: {
                        inputProps: { maxLength: 100 },
                        startAdornment: (
                          <InputAdornment position="start">
                            <img src={searchIcon} alt="loop" />
                          </InputAdornment>
                        ),
                        endAdornment: field.value && (
                          <InputAdornment position="end">
                            <IconButton
                              sx={{ fontSize: "1rem" }}
                              onClick={() => {
                                field.onChange("");
                                methods.clearErrors("search");
                              }}
                            >
                              x
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (value.length === 100) {
                        methods.setError("search", {
                          type: "maxLength",
                          message: "Search query cannot exceed 100 characters",
                        });
                      } else {
                        methods.clearErrors("search");
                      }

                      field.onChange(value);
                    }}
                  />

                  {error?.message && (
                    <div className="create-segment__input-wrap__segment-err">
                      <img src={errorIcon} alt="Error Icon" />
                      {error.message}
                    </div>
                  )}
                </label>
              );
            }}
          />
        </div>

        {areFiltersVisible && (
          <div className="segments__container__filters-row2">
            <div className="segments__container__filters-wrap__form-control-container-wrap">
              <Controller
                control={control}
                name="status"
                render={({ field }) => {
                  return (
                    <label className="filter-label" htmlFor="status-select">
                      Status
                      <MultiSelectDropdown
                        id="status-select"
                        label="Status"
                        options={statusOptions}
                        selectedValues={status}
                        onChange={handleStatusChange}
                      />
                    </label>
                  );
                }}
              />
            </div>
            <div className="segments__container__filters-wrap__form-control-container-wrap">
              <Controller
                control={control}
                name="createBy"
                render={({ field }) => {
                  return (
                    <label className="filter-label" htmlFor="createdBy-select">
                      Created by
                      <MultiSelectDropdown
                        id="createdBy-select"
                        label="Created By"
                        options={createdByOptions}
                        selectedValues={createdBy}
                        onChange={handleCreatedByChange}
                      />
                    </label>
                  );
                }}
              />
            </div>
            <div className="segments__container__filters-wrap__form-control-container-wrap">
              <Controller
                control={control}
                name="lastEditedBy"
                render={({ field }) => {
                  return (
                    <label className="filter-label" htmlFor="lastEdited-select">
                      Last edited by
                      <MultiSelectDropdown
                        id="lastEdited-select"
                        label="Last Edited by"
                        options={lastEditedByOptions}
                        selectedValues={lastEditedBy}
                        onChange={handleLastEditedChange}
                      />
                    </label>
                  );
                }}
              />
            </div>
            <div className="segments__container__filters-wrap__form-control-container-wrap">
              <Controller
                control={control}
                name="brand"
                render={({ field }) => {
                  return (
                    <label className="filter-label" htmlFor="brand-select">
                      Brand
                      <MultiSelectDropdown
                        id="brand-select"
                        label="Brand"
                        options={brandOptions}
                        selectedValues={brand}
                        onChange={handleBrandChange}
                      />
                    </label>
                  );
                }}
              />
            </div>
          </div>
        )}
      </div>

      <div className="segments__container__filters-wrap__right">
        <div className="segments__container__filters-wrap__show-filters">
          <button
            type="button"
            className="btn-secondary btn-border"
            onClick={toggleFiltersVisibility}
          >
            {!areFiltersVisible ? "Show Filters" : "Hide Filters"}
            <img
              className="btn-secondary__icon"
              src={instantmixIcon}
              alt={areFiltersVisible ? "Hide filters" : "Show filters"}
            />
          </button>
        </div>

        {areFiltersVisible && (
          <div className="segments__container__filters-wrap__clear-filters">
            <button
              type="button"
              id="clear-filters-button"
              name="clear-filters-button"
              className="btn-secondary no-border"
              onClick={clearFilters}
            >
              Clear
              <img className="btn-secondary__icon" src={CloseIconRed} alt="Clear filters" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SegmentsFilters;
